// navigation-completion — frontend API client for the per-submission navigation
// policy SNAPSHOT (the authorised "apply the org policy to this submission" path).
//
// Separate module from v2Client.ts; it reuses apiFetch so auth headers, base URL
// and error handling are shared. The backend owns the authority comparison
// (current|stale|missing|inactive) — the UI never re-derives it. A POST captures
// (initial) or re-captures (refresh a stale submission) the CURRENT org policy;
// a 409 means the org policy moved and the caller must re-apply deliberately.

import { apiFetch } from '@/api/v2Client';

export type NavigationSnapshotStatus = 'current' | 'stale' | 'missing' | 'inactive';

/** A jargon-free summary of what is captured (never hashes). */
export interface NavigationSnapshotSummaryWire {
  readonly capturedPolicyVersion: number;
  readonly capturedActivated: boolean;
  readonly capturedPreset: string;
  readonly capturedBy: string;
  readonly capturedAt: string | null;
}

/** The state returned by GET and by a successful POST. */
export interface NavigationSnapshotStateWire {
  readonly ok: true;
  readonly status: NavigationSnapshotStatus;
  readonly currentPolicyVersion: number;
  readonly currentPolicyActivated: boolean;
  readonly currentPreset: string;
  readonly authorizes: boolean;
  readonly snapshot: NavigationSnapshotSummaryWire | null;
}

export interface CaptureSnapshotBody {
  /** The exact org policy version being applied (optimistic concurrency). */
  readonly expectedPolicyVersion: number;
  /** Mandatory, recorded in the audit trail. */
  readonly reason: string;
}

function path(submissionId: string): string {
  return `/api/v1/submissions/${encodeURIComponent(submissionId)}/navigation-policy-snapshot`;
}

export const navigationSnapshot = {
  get: (submissionId: string): Promise<NavigationSnapshotStateWire> => apiFetch(path(submissionId)),
  capture: (submissionId: string, body: CaptureSnapshotBody): Promise<NavigationSnapshotStateWire> =>
    apiFetch(path(submissionId), { method: 'POST', json: body }),
};
